import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { useOnError } from 'src/utils/errors';
import { ICON_MAP } from 'src/utils/constants';
import { cn } from 'src/utils';
import { FINANCIAL_DOCUMENT_STATUS, FinancialDocument } from 'src/features/financialDocuments/utils/types';
import {
    EditDraftFinancialDocumentFormSchemaValues,
    FinancialDocumentFormSchemaValues,
    financialDocumentFormSchema,
} from 'src/features/financialDocuments/utils/formSchema';
import {
    FINANCIAL_DOCUMENT_RESOURCE,
    useGetFinancialDocument,
} from 'src/features/financialDocuments/api/useGetFinancialDocument';
import { financialDocumentsMessages } from 'src/features/financialDocuments/utils/messages';
import { queryClient } from 'src/lib/react-query';
import { LIST_FINANCIAL_DOCUMENTS_RESOURCE } from 'src/features/financialDocuments/api/useListFinancialDocuments';
import { toast } from 'src/hooks/useToast';
import { usePatchFinancialDocument } from 'src/features/financialDocuments/api/usePatchFinancialDocument';
import { useUpdateFinancialDocument } from 'src/features/financialDocuments/api/useUpdateFinancialDocument';
import FinancialDocumentForm from './FinancialDocumentForm';
import { getDirtyValues } from 'src/utils/getDirtyValues';

interface UpdateFinancialDocumentFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedFinancialDocument: FinancialDocument;
    children?: React.ReactNode;
    isOpen?: boolean;
}

function UpdateFinancialDocumentFormModal<TData>({
    selectedFinancialDocument,
    children,
    className,
}: UpdateFinancialDocumentFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const onError = useOnError();
    const { formatMessage } = useIntl();

    const { mutate: patchMutate } = usePatchFinancialDocument({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_FINANCIAL_DOCUMENTS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [FINANCIAL_DOCUMENT_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(financialDocumentsMessages.updateFinancialDocumentSuccess),
                description: formatMessage(financialDocumentsMessages.updateFinancialDocumentSuccessDescription),
            });
        },
        onError: (error) => {
            onError(error, form.setError);
        },
    });

    const { mutate: updateMutate, isPending } = useUpdateFinancialDocument({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_FINANCIAL_DOCUMENTS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [FINANCIAL_DOCUMENT_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(financialDocumentsMessages.updateFinancialDocumentSuccess),
                description: formatMessage(financialDocumentsMessages.updateFinancialDocumentSuccessDescription),
            });
        },
        onError: (error) => {
            onError(error, form.setError);
        },
    });

    const { data: financialDocument } = useGetFinancialDocument({
        documentId: selectedFinancialDocument?.id ?? '',
        config: { enabled: !!selectedFinancialDocument?.id && isModalOpen },
    });

    const isDraft = financialDocument?.status === FINANCIAL_DOCUMENT_STATUS.DRAFT;

    const defaultValues = React.useMemo(() => {
        return {
            type: financialDocument?.type ?? undefined,
            accounting_range: financialDocument?.accounting_range?.id ?? undefined,
            currency: financialDocument?.currency ?? undefined,
            public_description: financialDocument?.public_description ?? undefined,
            internal_description: financialDocument?.internal_description ?? undefined,
            tax_event_date: financialDocument?.tax_event_date ?? undefined,
            payment_due_date: financialDocument?.payment_due_date ?? undefined,
            issue_due_date: financialDocument?.issue_due_date ?? undefined,
            supplier: financialDocument?.supplier.id ?? undefined,
            customer: financialDocument?.customer.id ?? undefined,
            financial_document: financialDocument?.financial_document?.id ?? undefined,
            number: financialDocument?.number ?? undefined,
        };
    }, [financialDocument]);

    const form = useForm<FinancialDocumentFormSchemaValues>({
        resolver: zodResolver(financialDocumentFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [form, isModalOpen, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: EditDraftFinancialDocumentFormSchemaValues) => {
            const { type, ...remainingValues } = values;
            const dirtyFields = getDirtyValues(form.formState.dirtyFields, remainingValues);

            const ALWAYS_FIELDS = {
                customer: remainingValues.customer,
                supplier: remainingValues.supplier,
                currency: remainingValues.currency,
            };

            if (!isDraft) {
                patchMutate({ ...dirtyFields, documentId: selectedFinancialDocument?.id });
            }
            if (isDraft) {
                updateMutate({ ...dirtyFields, ...ALWAYS_FIELDS, documentId: selectedFinancialDocument?.id });
            }
        },
        [form.formState.dirtyFields, isDraft, patchMutate, selectedFinancialDocument?.id, updateMutate],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                        {formatMessage(financialDocumentsMessages.editFinancialDocumentSubtitle)}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={ICON_MAP.EDIT} />
                        <div>
                            <DialogTitle>
                                {formatMessage(financialDocumentsMessages.editFinancialDocumentSubtitle)}
                            </DialogTitle>
                            <DialogDescription>
                                {formatMessage(financialDocumentsMessages.editFinancialDocumentSubtitle)}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <FinancialDocumentForm form={form} onSubmit={onSubmit} isDraft={isDraft} isEditMode={true} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedFinancialDocument && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {formatMessage(financialDocumentsMessages.editFinancialDocumentSubtitle)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default UpdateFinancialDocumentFormModal;
